import { useStyletron } from '@thanx/uikit/theme'
import React, { useMemo } from 'react'
import { connect, ConnectedProps } from 'react-redux'

import { Icon, SelectHeader, SelectMenu } from 'controlled-components'
import { buildTranslate } from 'locales'
import {
  filterOptionsBySearch,
  selectTopData,
  selectTopIsOpen,
  selectTopSearchTerm,
} from 'slices/aiSegmentBuilder/picker'

import { PaginationContainer } from './Header'
import OptionsListContainer from './OptionsList.container'

const t = buildTranslate('components.ai.picker')

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface OwnProps {
  pickerId: number
  disabled?: boolean
  isLoading?: boolean
  goToPage?: (page: number) => void
}

type Props = OwnProps & ConnectedProps<typeof connector>

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Container for the dropdown menu.
 * Orchestrates header and options list sub-containers.
 * Shows loading spinner while data is being fetched.
 * Only renders when dropdown is open and not disabled.
 */
const MenuContainer: React.FC<Props> = ({
  pickerId,
  disabled = false,
  isLoading = false,
  goToPage,
  isOpen,
  data,
  searchTerm,
}) => {
  const [css, theme] = useStyletron()

  // Derived: an active search that matched nothing in the loaded set.
  const hasNoSearchMatches = useMemo(
    () =>
      searchTerm.trim() !== '' &&
      filterOptionsBySearch(data, searchTerm).length === 0,
    [data, searchTerm]
  )

  // Don't render if disabled
  if (disabled) {
    return null
  }

  const messageContainerClassName = css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    color: theme.colors.grey50,
  })

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={messageContainerClassName}>
          <Icon name="spinner" size={24} spin />
        </div>
      )
    }
    if (hasNoSearchMatches) {
      return (
        <div role="status" className={messageContainerClassName}>
          {t('no_matches')}
        </div>
      )
    }
    return <OptionsListContainer pickerId={pickerId} />
  }

  return (
    <SelectMenu isOpen={isOpen}>
      <SelectHeader>
        <SelectHeader.Left>
          <PaginationContainer pickerId={pickerId} goToPage={goToPage} />
        </SelectHeader.Left>
        <SelectHeader.Right />
      </SelectHeader>
      {renderContent()}
    </SelectMenu>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Redux Connection
// ═══════════════════════════════════════════════════════════════════════════

const mapStateToProps = (state: any, ownProps: OwnProps) => ({
  isOpen: selectTopIsOpen(state, ownProps.pickerId),
  data: selectTopData(state, ownProps.pickerId),
  searchTerm: selectTopSearchTerm(state, ownProps.pickerId),
})

const connector = connect(mapStateToProps)

export default connector(MenuContainer)
